/* [TAG-STYLE 2026-11-03, user] The "Clean label" tag of the Payments table's Tags column, also the ROW badge of the Refunds and
   Disputes tables (components/RowTagBadge). The manager on the old glossy pill with a coloured dot: "This pill with dot is pure
   AI looking, please change this, make it better." The user picked "Clean label" (option 2) on the comparison page
   https://claude.ai/artifact/FjEF7bCfb17HhRDg8TEZbF : one glass label with soft square corners, the TYPE in small coloured
   capitals and the amount in bold. No dot.
   Colour now means the type of money, not the department: all new money is Scale Ranking since 2026-10-27, so the old
   department colour put the same green dot on every new tag. Green = Retention, blue = New Sale / Upsell, grey = PIA, Recovery
   and Shopify. HubSpot orange (the only tag you can click) and Team ROW violet keep their colours.
   The small words after an amount are full ink like all secondary text ([FULL-INK] in styles/theme.ts), never grey. */
import React from 'react';
import styled, { DefaultTheme } from 'styled-components';
import { lgCardSheen } from '../styles/liquidGlass';

export type TagKind = 'retention' | 'sale' | 'other' | 'hubspot' | 'row' | 'split';

export const TAG_KIND: Record<TagKind, { rgb: string; inkDark: string; inkLight: string }> = {
  retention: { rgb: '19,183,144', inkDark: '#84e8af', inkLight: '#0f766e' },
  sale: { rgb: '59,130,246', inkDark: '#93c5fd', inkLight: '#1d4ed8' },
  other: { rgb: '111,116,114', inkDark: '#d4d4d4', inkLight: '#3d4140' },
  hubspot: { rgb: '255,122,89', inkDark: '#ffb39e', inkLight: '#c2410c' }, // HubSpot's brand orange (#ff7a59)
  row: { rgb: '139,92,246', inkDark: '#c4b5fd', inkLight: '#6d28d9' }, // [ROW-TAG 2026-08-21] the one hue no other tag uses
  split: { rgb: '245,158,11', inkDark: '#fde68a', inkLight: '#92400e' }, // a split website still to be mapped by PM
};

/** The colour of a mapping field, by its label (Retention, Recurring New Sale, One-Time Upsell, PIA …). */
export const tagKindOf = (label: string): TagKind =>
  (label === 'Retention' ? 'retention' : /New Sale|Upsell/.test(label) ? 'sale' : 'other');

/** An old brand's name, shown after its amount ("$400 · Tech"). Scale Ranking, where all new money goes, is not named. */
export const OLD_DEPT_NAME: Record<string, string> = { performance: 'Performance', tech: 'Tech', creative: 'Creative' };

type P = { theme: DefaultTheme };
const dk = (p: P) => p.theme.colorScheme === 'dark'; /* [SAGE-JADE] mode, not a colour match */
const ink = (p: P, kind: TagKind) => (dk(p) ? TAG_KIND[kind].inkDark : TAG_KIND[kind].inkLight);

/** The app's card shine over a flat layer of the kind's colour (a background-image value). */
export const tagLabelImage = (p: P, kind: TagKind, tint: number) =>
  `${lgCardSheen(p)}, linear-gradient(rgba(${TAG_KIND[kind].rgb}, ${tint}), rgba(${TAG_KIND[kind].rgb}, ${tint}))`;

/** The label's shape: 22 px tall (the old pill's height, so rows keep their size), soft square corners. */
export const TAG_LABEL_SHAPE = `
  display: inline-flex;
  align-items: center;
  gap: 7px;
  height: 22px;
  padding: 0 8px;
  border-radius: 6px;
  flex-shrink: 0;
  white-space: nowrap;
  line-height: 1;
`;

/** The glass: a light body, the kind's tint under the shine, a hairline edge in its colour and a light top line. */
export const tagLabelLook = (p: P, kind: TagKind, tint = 0.1, edge = 0.32) => `
  border: 1px solid rgba(${TAG_KIND[kind].rgb}, ${edge});
  background-color: ${dk(p) ? 'rgba(255, 255, 255, 0.08)' : 'rgba(255, 255, 255, 0.86)'};
  background-image: ${tagLabelImage(p, kind, tint)};
  box-shadow: inset 0 1px 0 ${dk(p) ? 'rgba(255, 255, 255, 0.10)' : '#ffffff'};
`;

export const TagLabel = styled.span<{ $kind: TagKind; $dashed?: boolean }>`
  ${TAG_LABEL_SHAPE}
  cursor: default;
  ${(p) => tagLabelLook(p, p.$kind)}
  ${(p) => (p.$dashed ? 'border-style: dashed;' : '')}
`;

/** The type: small coloured capitals. */
export const TagKindText = styled.span<{ $kind: TagKind }>`
  font-size: 9.5px;
  font-weight: 800;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  color: ${(p) => ink(p, p.$kind)};
`;

/** The amount: bold, same-width digits so amounts line up. Takes the kind's colour only on the HubSpot button. */
export const TagAmount = styled.span<{ $kind?: TagKind }>`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
  color: ${(p) => (p.$kind ? ink(p, p.$kind) : p.theme.text)};
`;

/** The small words after the amount (a PM's name, PIA months, an old brand): full ink, smaller and lighter. */
export const TagNote = styled.span`
  font-size: 10.5px;
  font-weight: 500;
  color: ${(p) => p.theme.text};
`;

const TagIconSvg = styled.svg`
  width: 11px;
  height: 11px;
  flex-shrink: 0;
  fill: none;
  stroke: currentColor;
  stroke-width: 2.6;
  stroke-linecap: round;
  stroke-linejoin: round;
`;
/** "Opens somewhere else": the HubSpot button with one deal. */
export const TagArrow: React.FC = () => (
  <TagIconSvg viewBox="0 0 24 24" aria-hidden="true"><path d="M8 16 16 8M9.5 8H16v6.5" /></TagIconSvg>
);
/** "Opens a list": the HubSpot button with 2 or more deals. */
export const TagChevron: React.FC = () => (
  <TagIconSvg viewBox="0 0 24 24" aria-hidden="true" style={{ width: 10, height: 10 }}><path d="m7 10 5 5 5-5" /></TagIconSvg>
);

/** One tag: the type, then the amount and its small words. A tag can have only words (Split website) or only the type (ROW). */
export const TagBadge: React.FC<{ kind: TagKind; label: string; value?: string; note?: string; title?: string; dashed?: boolean }> = ({
  kind, label, value, note, title, dashed,
}) => (
  <TagLabel $kind={kind} $dashed={dashed} title={title}>
    <TagKindText $kind={kind}>{label}</TagKindText>
    {(value || note) && (
      <TagAmount>
        {value}
        {note && <TagNote>{value ? `· ${note}` : note}</TagNote>}
      </TagAmount>
    )}
  </TagLabel>
);
